import { useBetween } from 'use-between';
import styles from './CartBasketTable.module.scss';
import globalStyles from '../../../global-styles.module.scss';
import { ShoppingCartUseBetween } from '../../../components/order/use-between/shopping-cart-use-between';
import React, { useEffect, useState } from 'react';
import ProductThumbnail from '../../../shared/image-viewer/product-thumbnail';
import classNames from 'classnames';
import { useTranslation } from '../../../i18n/useTranslation';
import {
    getPriceWithPrecision,
    getTotalQuantityShoppingCart,
    removeAll,
    removeProductById,
    updateQuantity,
} from '../../../components/shopping-cart/services/shopping-cart.service';
import { htmlId } from './cart-basket-table.service';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { IShopingCart } from '../../../components/order/interfaces/shopping-cart.interface';
import { getCurrencySymbol } from '../../../components/order/services/currency.service';
import CalculationShippingCosts from '../calculation-shipping-costs/calculation-shipping-costs';
import { IShopingCartProduct } from '../../../components/order/interfaces/shopping-cart-product.interface';
import { Input } from '@mui/material';
import { Utils } from '../../../shared/constants/utilities.constants';
import QuantityInput from '../../../shared/input/quantity-input ';
import { claimIsActive } from '../../../shared/services/claims.service';
import { PermissionsUseBetween } from '../../../shared/use-between/permissions-use-between';
import { ErpClaimCode } from '../../../shared/models/erp-claim-code.enum';

interface WarningState {
    [productId: string]: string;
}
interface QuantityState {
    [productId: string]: number;
}

const CartBasketTable: React.FC = () => {
    const { t } = useTranslation('translation');
    const { shoppingCart, setShoppingCart, setQuantityShoppingCart } = useBetween(ShoppingCartUseBetween);
    const { setIsReadyToOrder } = useBetween(ShoppingCartUseBetween);
    const { setBuildingSiteAddress, setBuyerContact } = useBetween(ShoppingCartUseBetween);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const [warnings, setWarnings] = useState<WarningState>({});
    const [quantities, setQuantities] = useState<QuantityState>({});
    const { claims } = useBetween(PermissionsUseBetween);

    useEffect(() => {
        setWarnings(
            Object.fromEntries(
                shoppingCart.products.map((product: IShopingCartProduct) => [product.productChildUuid, ''])
            )
        );
        setQuantities(
            Object.fromEntries(
                shoppingCart.products.map((product: IShopingCartProduct) => [
                    product.productChildUuid,
                    product.quantity,
                ])
            )
        );
    }, [shoppingCart]);

    const handleResponse = (shopingCart: IShopingCart) => {
        setShoppingCart(shopingCart);
        setQuantityShoppingCart(getTotalQuantityShoppingCart(shopingCart));
        setIsReadyToOrder(shopingCart.isReadyToOrder);
        // buildingSiteAddress/buyerContact are separate shared state (see ShoppingCartUseBetween) - the
        // delivery address/contact selects read from these, not from shoppingCart itself, so every action
        // that can change them (remove all clears them, a quantity change can flip isReadyToOrder around
        // them) must refresh them too, or they only catch up on the next full page load.
        setBuildingSiteAddress(shopingCart.buildingSiteAddress);
        setBuyerContact(shopingCart.buyerContact);
    };

    const handlRemoveProductById = (productId: string) => {
        removeProductById(productId).then((response) => {
            if (response) {
                handleResponse(response.data);
            }
        });
    };

    const handlRemoveAll = () => {
        removeAll().then((response) => {
            if (response) {
                handleResponse(response.data);
            }
        });
    };

    const setAndGetWarnings = (newQuantity: number, minQuantity: number, productChildUuid: string): WarningState => {
        const _newQuantity: number = newQuantity || 1;
        const _warrnings = warnings;
        if (_newQuantity < minQuantity) {
            _warrnings[productChildUuid] = 'commonErrors.MIN_QUANTITY';
            setWarnings((prevWarnings) => ({
                ...prevWarnings,
                [productChildUuid]: 'commonErrors.MIN_QUANTITY',
            }));
        } else if (_newQuantity % minQuantity) {
            _warrnings[productChildUuid] = 'commonErrors.NOT_MATCHING_PACKAKING_SIZE';
            setWarnings((prevWarnings) => ({
                ...prevWarnings,
                [productChildUuid]: 'commonErrors.NOT_MATCHING_PACKAKING_SIZE',
            }));
        } else {
            _warrnings[productChildUuid] = '';
            setWarnings((prevWarnings) => ({
                ...prevWarnings,
                [productChildUuid]: '',
            }));
        }
        return _warrnings;
    };

    const handlUpdateQuantity = (productId: string, newQuantity: number, minQuantity: number) => {
        const _warrnings = setAndGetWarnings(newQuantity, minQuantity, productId);
        setQuantities((prevQuantities) => ({
            ...prevQuantities,
            [productId]: newQuantity,
        }));
        if (_warrnings[productId].length === 0) {
            updateQuantity(productId, newQuantity).then((response) => {
                if (response) {
                    handleResponse(response.data);
                }
            });
        }
    };

    const showCalcShippingCosts = (): boolean => {
        return (
            claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) &&
            shoppingCart?.products &&
            shoppingCart.products.length > 0 &&
            !shoppingCart?.detail?.isSelfPickup &&
            !Utils.isNullOrUndefined(shoppingCart?.transportType) &&
            !Utils.isNullOrUndefined(shoppingCart?.amountFreeDelivery)
        );
    };

    const warningsExist = (item: IShopingCartProduct): boolean => {
        return (
            !Utils.isNullOrUndefined(warnings) &&
            !Utils.isNullOrUndefined(warnings[item.productChildUuid]) &&
            warnings[item.productChildUuid].length > 0
        );
    };

    return (
        <div className={styles.table_container}>
            {shoppingCart &&
                shoppingCart.products &&
                shoppingCart.products.map((item, index) => (
                    <div className={styles.row_content} key={index}>
                        <div className={classNames(styles.cell_content, globalStyles.ml_5)}>
                            <ProductThumbnail productId={item.productParentUuid}></ProductThumbnail>
                        </div>
                        <div className={styles.cell_content}>
                            <section>
                                <label className={classNames(styles.header1, globalStyles.float_left_item)}>
                                    {t('MIN_QUANTITY')}
                                </label>
                                {item.minQuantity}
                            </section>
                        </div>
                        <div className={styles.cell_content}>
                            <section>
                                <label className={classNames(styles.header1, globalStyles.float_left_item)}>
                                    {t('cart.quantity')}
                                </label>
                                <div className={classNames(globalStyles.float_left_item, globalStyles.mr_3)}>
                                    <QuantityInput
                                        key={item.productChildUuid}
                                        quantity={quantities[item.productChildUuid] ?? 1}
                                        minQuantity={item.minQuantity}
                                        step={item.orderQuantity}
                                        warningMessage={
                                            warningsExist(item) ? t(warnings[item.productChildUuid]) : undefined
                                        }
                                        onChange={(newQty) =>
                                            handlUpdateQuantity(item.productChildUuid, newQty, item.minQuantity)
                                        }
                                    />
                                </div>
                            </section>
                        </div>
                        <div className={styles.cell_content}>
                            <section>
                                <label className={classNames(styles.header1, globalStyles.float_left_item)}>
                                    {t('ARTICLE_CODE')}
                                </label>
                                <label className={classNames(styles.text, globalStyles.float_left_item)}>
                                    {item.articleCode}
                                </label>
                            </section>
                        </div>
                        <div className={styles.cell_content}>
                            <section>
                                <label className={classNames(styles.header1, globalStyles.float_left_item)}>
                                    {t('VARIANT_NAME')}
                                </label>
                                <label className={classNames(styles.text, globalStyles.float_left_item)}>
                                    {item.productName}
                                </label>
                            </section>
                        </div>
                        <div className={styles.cell_content}>
                            <section className={styles.remove_btn_section}>
                                <button
                                    id={`${htmlId}-${index}-remove-product-btn`}
                                    style={{ height: '30px' }}
                                    type="button"
                                    className={classNames(
                                        globalStyles.defult_btn,
                                        globalStyles.mr_2,
                                        globalStyles.float_left_item,
                                        globalStyles.mb_2,
                                        globalStyles.flex_center_item
                                    )}
                                    onClick={() => {
                                        handlRemoveProductById(item.productChildUuid);
                                    }}
                                    onMouseOver={() => {
                                        setHoverIndex(index);
                                    }}
                                    onMouseLeave={() => {
                                        setHoverIndex(null);
                                    }}
                                >
                                    {index === hoverIndex ? t('REMOVE') : <RemoveCircleIcon fontSize="small" />}
                                </button>
                                {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) && (
                                    <React.Fragment>
                                        <label className={classNames(styles.header1, globalStyles.float_left_item)}>
                                            {t('NET_PRICE')}
                                        </label>
                                        <label className={classNames(styles.text, globalStyles.float_left_item)}>
                                            <span className={globalStyles.mr_1}>
                                                {getPriceWithPrecision(item.nettoTotalPrice)}
                                            </span>
                                            <span>{getCurrencySymbol(item.saleCurrencyName)}</span>
                                        </label>
                                    </React.Fragment>
                                )}
                            </section>
                        </div>
                    </div>
                ))}
            {showCalcShippingCosts() && (
                <CalculationShippingCosts
                    transportType={shoppingCart.transportType as string}
                    amountFreeDelivery={shoppingCart.amountFreeDelivery as number}
                ></CalculationShippingCosts>
            )}
            <section
                className={classNames(
                    globalStyles.float_right_item,
                    globalStyles.default_border_top,
                    globalStyles.position_rel,
                    globalStyles.mb_2
                )}
            >
                <button
                    id={`${htmlId}-clear-basket-btn`}
                    type="button"
                    disabled={shoppingCart.products.length === 0}
                    onClick={() => {
                        handlRemoveAll();
                    }}
                    className={classNames(globalStyles.defult_btn, globalStyles.mt_2)}
                >
                    {t('CLEAR_BASKET')}
                </button>
                {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) && (
                    <div className={classNames(styles.summary_content, styles.summary_separate_line)}>
                        <label className={classNames(styles.header2)}>{t('TOTAL')}:</label>
                    </div>
                )}
            </section>
            {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) && (
                <section className={classNames(globalStyles.float_left_item)}>
                    <div>
                        <label className={classNames(styles.header1, globalStyles.float_left_item)}>
                            {t('NET_PRICE')}
                        </label>
                        <label
                            className={classNames(
                                styles.text,
                                globalStyles.float_left_item,
                                styles.summary_separate_line
                            )}
                        >
                            <span className={globalStyles.mr_1}>
                                {getPriceWithPrecision(shoppingCart.nettoTotalPrice)}
                            </span>
                            <span>
                                {Array.isArray(shoppingCart.products) && shoppingCart.products.length > 0
                                    ? getCurrencySymbol(shoppingCart.products[0].saleCurrencyName)
                                    : '_'}
                            </span>
                        </label>
                        <section
                            className={classNames(globalStyles.float_left_item, styles.custom_shipping_cost_content)}
                        >
                            <div className={globalStyles.mr_5}>
                                <label className={classNames(styles.header1, globalStyles.float_left_item)}>
                                    {t('SHIPPING_COST')}
                                </label>
                                <label className={classNames(styles.text, globalStyles.float_left_item)}>
                                    <span className={globalStyles.mr_1}>
                                        {getPriceWithPrecision(shoppingCart.shippingCost)}
                                    </span>
                                    <span>
                                        {Array.isArray(shoppingCart.products) && shoppingCart.products.length > 0
                                            ? getCurrencySymbol(shoppingCart.products[0].saleCurrencyName)
                                            : '_'}
                                    </span>
                                </label>
                            </div>
                            <div>
                                {shoppingCart && shoppingCart.shippingCostMessage && (
                                    <div className={globalStyles.float_left_item}>
                                        <label className={styles.header3}>{t(shoppingCart.shippingCostMessage)}</label>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                </section>
            )}
        </div>
    );
};
export default CartBasketTable;
